import { useMemo, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Filters, createFieldDefs } from '@workboard/ui-list-filters';
import axios from 'axios';
import {
  Popover, ThemeProvider, createTheme, popoverClasses,
} from '@mui/material';
import { mergeThemeOptions, themeOptions } from '@workboard/wobo-material';
import {
  useCustomFields,
  mapCustomFieldToFieldDef,
  buildCustomFieldInitialCriteria,
  mapCustomFieldsToParams,
} from './useCustomFields';
import { ALIGNMENT_FILTER_EX_DATE, ALIGNMENT_FILTER_STATUS } from '../../../constants/okr/alignmentFilter.constants';
import {
  buildTimePeriodInitialCriterion,
  getTimePeriodFieldDef,
  mapTimePeriodCriterionToParams,
} from '../../../utils/timePeriodFilter.util';

// =============================================================================
// Hook: useUserTeams - Fetches teams data with chipFilter hardcoded to 'my'
// Pattern from TeamsTable.tsx
// =============================================================================

const useUserTeams = () => {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTeams = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const config = {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params: {
          chipFilter: 'my', 
        },
      };

      const response = await axios.get('/wb/teamUserSearch/landing', config);

      if (response.data?.data) {
        setTeams(response.data.data);
      } else {
        setTeams([]);
      }
    } catch (err) {
      setError(err);
      console.error('Failed to fetch teams:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  return { teams, isLoading, error, refetch: fetchTeams };
};

const StyledPopover = styled(Popover)({
  [`.${popoverClasses.paper}`]: {
    borderRadius: '8px',
    padding: '4px 16px 16px',
    marginTop: '5px',
    width: '650px',
    maxHeight: 'calc(100vh - 120px)', // in case the height is larger than the viewport it won't block the button
  },
});

// Theme overrides to prevent global styles from affecting Filters component
const filtersThemeExtensions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: 'auto',
          minHeight: '40px',
        },
        input: {
          height: 'auto',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 'auto',
        },
        input: {
          height: '24px',
          padding: '8.5px 14px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          height: 'auto',
          minHeight: '1.4375em',
        },
      },
    },
  },
};

const filtersTheme = createTheme(mergeThemeOptions(themeOptions, filtersThemeExtensions));

const getFieldDefs = (t, customFields = []) => {
  const staticFields = {
    status: {
      label: t('Objective Status'),
      type: 'multi-select-button',
      operators: ['is'],
      options: () => [
        { label: t('Open'), value: ALIGNMENT_FILTER_STATUS.OPEN },
        { label: t('Closed'), value: ALIGNMENT_FILTER_STATUS.CLOSED },
      ],
    },
    timePeriod: getTimePeriodFieldDef(t),
  };

  const customFieldDefs = customFields.reduce((acc, field) => {
    acc[`customField-${field.id}`] = mapCustomFieldToFieldDef(field);
    return acc;
  }, {});

  return createFieldDefs({
    fields: {
      ...staticFields,
      ...customFieldDefs,
    },
  });
};

const getInitialValues = (t, alignmentFilters, customFields) => {
  const {
    startDate,
    endDate,
    openOkr = true,
    closedOkr = false,
    exDate = ALIGNMENT_FILTER_EX_DATE.RANGE,
    customFields: storedCustomFieldsJson,
  } = alignmentFilters || {};

  // Parse stored custom fields
  let customFieldCriteria = [];
  if (storedCustomFieldsJson) {
    try {
      const storedCustomFields = JSON.parse(storedCustomFieldsJson);
      customFieldCriteria = buildCustomFieldInitialCriteria(storedCustomFields, customFields);
    } catch {
      // Invalid JSON
    }
  }

  return ({
    group: {
      operator: 'AND',
      criteria: [
        {
          fieldName: 'status',
          fieldType: 'multi-select-button',
          operator: 'is',
          values: [
            openOkr ? { label: t('Open'), value: ALIGNMENT_FILTER_STATUS.OPEN } : null,
            closedOkr ? { label: t('Closed'), value: ALIGNMENT_FILTER_STATUS.CLOSED } : null,
          ].filter(Boolean),
          nonRemovable: true,
        },
        buildTimePeriodInitialCriterion({
          startDate,
          endDate,
          exDate,
        }),
        ...customFieldCriteria,
      ],
    },
  })
};

// =============================================================================
// Helpers
// =============================================================================

/**
 * Maps Filters component status values to API params shape
 */
const mapStatusToParams = (filterValues) => {
  const criteria = filterValues?.group?.criteria || [];
  const statusCriteria = criteria.find((c) => c.fieldName === 'status');
  const values = statusCriteria?.values?.map((v) => v.value) || [];

  const status = values.join(',');

  return {
    status,
    closedOkr: values.includes(ALIGNMENT_FILTER_STATUS.CLOSED),
    openOkr: values.includes(ALIGNMENT_FILTER_STATUS.OPEN),
  };
};

const AlignmentFilter = (props) => {
  const {
    open,
    close,
    anchorEl,
    alignmentFilters,
    loadGoalAlignCallback,
    saveAlignmentFilters,
    teamId,
    userId
  } = props;
  const { t } = useTranslation();

  // Use new hook to fetch teams with chipFilter='my'
  const { teams } = useUserTeams();
  const teamIds = useMemo(() => teams.map((team) => String(team.id)), [teams]);

  // Filter custom fields by user's accessible teams
  const { customFields, customFieldIds } = useCustomFields({ teamIds });
  const fieldDefs = useMemo(
    () => getFieldDefs(t, customFields),
    [t, customFields]
  );
  const initialValues = useMemo(
    () => getInitialValues(t, alignmentFilters, customFields),
    [t, alignmentFilters, customFields]
  );

  const handleSubmit = (event = {}) => {
    const statusParams = mapStatusToParams(event?.values);
    const timePeriodParams = mapTimePeriodCriterionToParams(event?.values);
    const customFieldsParams = mapCustomFieldsToParams(event?.values, customFieldIds);

    const params = {
      ...statusParams,
      ...timePeriodParams,
      ...customFieldsParams,
    };

    // Always pass current team/user so previously used team is reused;
    // only cleared on explicit Reset
    const appliedTeamId = teamId ?? null;
    const appliedUserId = userId ?? null;
    loadGoalAlignCallback({ ...params, teamId: appliedTeamId, userId: appliedUserId });

    saveAlignmentFilters({ ...params, teamId: appliedTeamId, userId: appliedUserId });
    close();
  };

  const handleReset = () => {
    // Clear all filters and team/user only when user explicitly resets
    const emptyParams = {
      startDate: '',
      endDate: '',
      exDate: ALIGNMENT_FILTER_EX_DATE.RANGE,
      openOkr: true,
      closedOkr: false,
      status: ALIGNMENT_FILTER_STATUS.OPEN,
      teamId: null,
      userId: null,
    };
    loadGoalAlignCallback(emptyParams);
    saveAlignmentFilters({});
    close();
  };

  if (!open) {
    return null;
  }

  return (
    <ThemeProvider theme={filtersTheme}>
      <StyledPopover
        anchorEl={anchorEl}
        open={open}
        onClose={() => close()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Filters
          fieldDefs={fieldDefs}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onClose={() => close()}
          variant="compact"
        />
      </StyledPopover>
    </ThemeProvider>
  );
};

export default AlignmentFilter;
